import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { createCsv } from '@fitogest/reactive-form-builder/csv';
import { FormType } from '@fitogest/reactive-form-builder/interfaces';
import { ColInterface } from '../../../../common/components/common-list-table/col.interface';
import { ColStylesInterface } from '../../../../common/components/common-list-table/col-styles.interface';
import { StorageManager } from '../../../../common/storage-manager.class';
import { RequestHandler } from '../../../../service/OffService/request-handler';
import { Filtering } from '../../../../service/filtering/filtering';
import { TareasApiService } from '../../../../service/api/tareas-api.service';
import { DashboardService } from '../../../../service/events/dashboard.service';
import { BaseView } from '../../../base-view';
import { environment } from '../../../../../environments/environment';
import { TareasBioModel } from '../../../../models/tareas/tareas-bio.model';
import { TareasBioFormDefinition } from './tareas_bio-form/form-control/form-definition';

@Component({
    selector: 'app-tareas-bio',
    standalone: false,
    templateUrl: './tareas_bio.component.html',
    styleUrls: ['./tareas_bio.component.scss'],
})
export class TareasBioComponent extends BaseView<TareasBioModel> implements OnInit {
    public readonly appName = environment.appName;
    public readonly serverUrl = environment.serverUrl;
    public readonly rolUser = StorageManager.getUser().rol;
    public readonly fincaName = environment.features.fincaName;
    public readonly sectorName = environment.features.sectorName;
    public readonly parcelaName = environment.features.parcelaName;
    public readonly hasClients = environment.features.hasClients;
    public readonly showTareasBioExportCsv = environment.features.showTareasBioExportCsv;
    public readonly userCanSeeClientes = this.hasClients &&
        (environment.features.userCanSeeClientes || StorageManager.getUser().rol === 'admin');

    public form: TareasBioFormDefinition | null = null;

    public getRequest: RequestHandler<TareasBioModel[]> = this.tareasApi.bio.GET;
    public deleteRequest: RequestHandler<TareasBioModel> = this.tareasApi.bio.DELETE;
    public pathToForm = 'crear-bio';
    public menuItemId = 'bio';

    public override filtering: Filtering<TareasBioModel> = new Filtering<TareasBioModel>();
    public override canShow: Record<string, boolean> = {};
    public override model: TareasBioModel = {};

    public cols: ColInterface[] = [
        { field: 'id', header: 'Nº\xa0Ficha', filter: '' },
        { field: 'fecha_receta', header: 'F.\xa0Receta', filter: '' },
        { field: 'fecha_aplicacion', header: 'F.\xa0Apl.', filter: '', visible: this.appName !== 'hernandorena' },
        { field: 'finca', header: this.fincaName, filter: '' },
        { field: 'sector', header: this.sectorName, filter: '' },
        { field: 'parcela', header: this.parcelaName, filter: '' },
        { field: 'producto', header: 'Producto', filter: '' },
        { field: 'modo_accion', header: this.appName === 'hernandorena' ? 'Tipo medida' : 'Modo de\xa0acción', filter: '' },
        { field: 'forma_aplicacion', header: 'Forma de\xa0aplicación', filter: '' },
        { field: 'dosis', header: 'Dosis', filter: '' },
        { field: 'tipo_plaga', header: 'Tipo de plaga', filter: '', visible: this.appName === 'hernandorena' },
        { field: 'gasto', header: 'Gasto', filter: '', visible: this.appName === 'hernandorena' },
        { field: 'm2', header: 'm²', filter: '', visible: this.appName === 'hernandorena' },
        { field: 'cantidad_total_producto', header: 'Cantidad total producto', filter: '', visible: this.appName === 'hernandorena' },
        { field: 'facturado', header: 'Facturado', filter: '', visible: this.appName === 'agrosalvi' },
    ];

    constructor(
        public tareasApi: TareasApiService,
        private dashboard: DashboardService,
        private router: Router,
    ) {
        super(dashboard, tareasApi.bio.GET, tareasApi.bio.DELETE);
    }

    public ngOnInit(): void {
        this.dashboard.setSubmenuItem('tareas', this.menuItemId);
    }

    public add(): void {
        if (!StorageManager.getClient() && this.userCanSeeClientes) {
            this.dashboard.openClientDialog();
            return;
        }

        this.router.navigate(['dashboard', this.pathToForm]);
    }

    public edit(data: TareasBioModel): void {
        this.router.navigate(['dashboard', 'editar-bio', data.id, false]);
    }

    public duplicate(data: TareasBioModel): void {
        this.router.navigate(['dashboard', 'duplicar-bio', data.id]);
    }

    public override show(modal: string, data: TareasBioModel): void {
        super.show(modal, data);

        const formType = modal === 'delete' ? +FormType.DELETION : +FormType.INFORMATIVE;

        this.form = new TareasBioFormDefinition(formType, data);
    }

    public override hide(modal: string): void {
        super.hide(modal);
        this.form = null;
    }

    public downloadCsv(): void {
        this.form = new TareasBioFormDefinition(+FormType.EXPORT, {});

        const csvContent = createCsv(
            this.form.fieldNames,
            this.form.fieldLabels,
            this.filtering.value as TareasBioModel[],
        );
        const csvLink = document.getElementById('csvLink') as HTMLAnchorElement | null;

        if (!csvLink) {
            return;
        }

        csvLink.download = `${this.form.formFields.name}.csv`;
        csvLink.href = encodeURI(csvContent);
        csvLink.click();
    }

    public globalConditionalStyle(_value: string, col: ColInterface): ColStylesInterface {
        if (col.field === 'trabajadores_nombres' || col.field === 'observaciones') {
            return {
                style: {
                    'max-width': '50px',
                },
            };
        }

        return {};
    }

    public globalVisualTransform(value: string, col: ColInterface): string {
        if (col.field === 'parcela') {
            return value.length > 50 ? value.slice(0, 49) + '…' : value;
        }

        if (col.field === 'facturado') {
            return value === '1' ? 'Sí' : 'No';
        }

        return value;
    }
}
